import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import { metricsAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { usePageTitle } from "../contexts/PageTitleContext";
import MetricForm from "../components/metrics/MetricForm";

const MetricsGalleryPreview = () => {
  const { id } = useParams();
  const location = useLocation();
  const { currentWorkspace } = useAuth();
  const { showToast } = useToast();
  const { setPageTitle, setParentPage, setShowBackButton } = usePageTitle();

  const [metric, setMetric] = useState(location.state?.template || null);
  const [loading, setLoading] = useState(!location.state?.template);

  useEffect(() => {
    setPageTitle("Gallery Metric");
    setParentPage({ name: "Metrics Gallery", href: "/evaluation/metrics/gallery" });
    setShowBackButton(true);
    return () => {
      setPageTitle(null);
      setParentPage(null);
      setShowBackButton(false);
    };
  }, [setPageTitle, setParentPage, setShowBackButton]);

  // On direct load / refresh there's no router state — fetch the gallery and
  // pick this template out of it (no single-template gallery endpoint exists).
  useEffect(() => {
    if (metric || !currentWorkspace?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await metricsAPI.getGallery(currentWorkspace.id);
        const found = (res.data || []).find((m) => String(m.id) === String(id));
        if (!cancelled) {
          if (found) setMetric(found);
          else showToast("Gallery metric not found", "error");
        }
      } catch (e) {
        if (!cancelled) showToast("Failed to load gallery metric", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, currentWorkspace, metric, showToast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-foreground" />
      </div>
    );
  }

  if (!metric) {
    return (
      <div className="px-4 py-16 text-center text-sm text-muted-foreground">
        This gallery metric could not be found.
      </div>
    );
  }

  return (
    <div className="px-4">
      <div className="mb-4 flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
        <Lock className="size-4 shrink-0 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          This is a curated gallery template — read-only here. It can only be
          edited from the admin panel. Use{" "}
          <span className="font-medium text-foreground">Add to more</span> on the
          gallery to put a copy in your workspace, then edit that copy.
        </p>
      </div>

      <MetricForm
        mode="edit"
        initialMetric={metric}
        showAssignment={false}
        canWrite={false}
        readOnly
        clientId={currentWorkspace?.id}
      />
    </div>
  );
};

export default MetricsGalleryPreview;
